import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Calendar, dayjsLocalizer } from 'react-big-calendar';
import dayjs from 'dayjs';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './MeetingCalendar.scss';
import { getCalendarEvents } from '../../api/ctrag'; // Assuming API path
import { message, Spin, Popover, Button } from 'antd';
import SettingContext from '../../contexts/settingProvider';
import EventDetailsDisplay from './EventDetailsDisplay'; // Added import
import LoadingAnimation from '../LoadingAnimation';
import { useTranslation } from 'react-i18next';

const localizer = dayjsLocalizer(dayjs);

// Custom hook for window size
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

// Component for Popover Content
const EventPopoverContent = ({ eventResource, onSelectButtonClick, t, windowSize }) => {
  const isSmallScreen = windowSize.width < 1024;
  const isShortScreen = windowSize.height < 600;
  
  return (
    <div style={{ 
      maxWidth: isSmallScreen ? Math.min(350, windowSize.width - 40) : 350,
      minWidth: isSmallScreen ? Math.min(280, windowSize.width - 40) : 280,
      maxHeight: isShortScreen ? windowSize.height - 100 : 'auto'
    }}>
      <div style={{ 
        maxHeight: isShortScreen ? windowSize.height - 180 : isSmallScreen ? '95vh' : 'auto',
        overflowY: 'auto'
      }}>
        <EventDetailsDisplay eventResource={eventResource} />
      </div>
      <Button
        type="primary"
        size={isSmallScreen ? "small" : "middle"}
        style={{ 
          marginTop: '10px', 
          width: '100%',
          position: 'sticky',
          bottom: 0,
          zIndex: 1
        }}
        onClick={() => onSelectButtonClick(eventResource)}
      >
        {t('aiAssistant.meetingSummary.form.calendar.selectMeeting')}
      </Button>
    </div>
  );
};

// Custom Event Wrapper for react-big-calendar
const CustomEventWrapper = ({ event, onSelectEvent: onSelectEventFromCalendarProps, windowSize }) => {
  const { t } = useTranslation();
  
  // 'event' is from react-big-calendar, containing 'resource' (our original event)
  // 'onSelectEventFromCalendarProps' is the callback passed to MeetingCalendar from MeetingSummaryForm
  
  // No longer stopping propagation here, as Popover itself handles click
  // const handleWrapperClick = (e) => {
  //   e.stopPropagation(); 
  // };

  const isSmallScreen = windowSize.width < 1024;

  return (
    // The Popover now directly wraps the event content and is triggered by click.
    // <div onClick={handleWrapperClick} style={{ height: '100%' }}> 
      <Popover
        content={<EventPopoverContent eventResource={event.resource} onSelectButtonClick={onSelectEventFromCalendarProps} t={t} windowSize={windowSize} />}
        title={<div style={{fontWeight: 'bold', paddingBottom: '5px', borderBottom: '1px solid #f0f0f0', fontSize: '14px'}}>{t('aiAssistant.meetingSummary.form.calendar.meetingDetails')}</div>}
        trigger="click"
        placement={isSmallScreen ? "top" : "auto"}
        classNames={{
          root: `event-popover ${event.resource?.isAllDay ? 'all-day-event-popover' : ''}`
        }}
        styles={{
          root: {
            maxWidth: isSmallScreen ? '95vw' : 'auto',
            zIndex: 1070
          }
        }}
        getPopupContainer={() => document.body}
        destroyTooltipOnHide={true}
      >
        {/* This div is what RBC renders as the event. It should be the click target for Popover. */}
        <div className="rbc-event-content" title={event.title} style={{ cursor: 'pointer' }}> {/* Changed cursor to pointer */}
            {event.title}
        </div>
      </Popover>
    // </div>
  );
};

const MeetingCalendar = ({ onSelectEvent, googleToken, fields = 'extended' }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const { dark } = useContext(SettingContext);
  const { t } = useTranslation();
  const windowSize = useWindowSize();

  useEffect(() => {
    const fetchEvents = async (date) => {
      if (!googleToken) {
        message.error('Missing Google Token. Cannot fetch calendar events.');
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // Calculate timeMin and timeMax for the current month view
        const firstDayOfMonth = dayjs(date).startOf('month').toISOString();
        const lastDayOfMonth = dayjs(date).endOf('month').toISOString();

        const fetchedEvents = await getCalendarEvents({
          start: firstDayOfMonth,
          end: lastDayOfMonth,
          fields: fields,
          // Potentially add other params like q for search if needed later
        });

        if (fetchedEvents && Array.isArray(fetchedEvents)) {
          const formattedEvents = fetchedEvents.map(event => ({
            id: event.id,
            title: event.title || '(No Title)',
            start: dayjs(event.start).toDate(),
            end: dayjs(event.end).toDate(),
            allDay: event.isAllDay || false,
            resource: event,
          }));
          setEvents(formattedEvents);
        } else if (fetchedEvents && fetchedEvents.items && Array.isArray(fetchedEvents.items)) {
          const formattedEvents = fetchedEvents.items.map(event => ({
            id: event.id,
            title: event.summary || '(No Title)',
            start: dayjs(event.start.dateTime || event.start.date).toDate(),
            end: dayjs(event.end.dateTime || event.end.date).toDate(),
            allDay: !!event.start.date,
            resource: event,
          }));
          setEvents(formattedEvents);
        } else {
          setEvents([]);
          // message.info('No events found for this period or unable to fetch.');
        }
      } catch (error) {
        console.error('Error fetching calendar events:', error);
        message.error(t('aiAssistant.meetingSummary.form.calendar.loadEventsFailed') + (error.message || t('aiAssistant.meetingSummary.form.calendar.unknownError')));
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents(currentDate);
  }, [googleToken, currentDate, fields, t]);

  const handleNavigate = (newDate) => {
    setCurrentDate(newDate);
  };

  const { formatos, messages } = useMemo(() => ({
    formatos: {
      monthHeaderFormat: t('aiAssistant.meetingSummary.form.calendar.formats.monthHeader'),
      dayHeaderFormat: t('aiAssistant.meetingSummary.form.calendar.formats.dayHeader'),
      dayRangeHeaderFormat: ({ start, end }, culture, local) =>
        `${local.format(start, t('aiAssistant.meetingSummary.form.calendar.formats.dayRange'), culture)} – ${local.format(end, t('aiAssistant.meetingSummary.form.calendar.formats.dayRange'), culture)}`,
      agendaHeaderFormat: ({ start, end }, culture, local) =>
      `${local.format(start, t('aiAssistant.meetingSummary.form.calendar.formats.agendaHeader'), culture)} – ${local.format(end, t('aiAssistant.meetingSummary.form.calendar.formats.agendaHeader'), culture)}`,
      timeGutterFormat: t('aiAssistant.meetingSummary.form.calendar.formats.timeGutter'),
    },
    messages: {
      week: t('aiAssistant.meetingSummary.form.calendar.messages.week'),
      work_week: t('aiAssistant.meetingSummary.form.calendar.messages.workWeek'),
      day: t('aiAssistant.meetingSummary.form.calendar.messages.day'),
      month: t('aiAssistant.meetingSummary.form.calendar.messages.month'),
      previous: t('aiAssistant.meetingSummary.form.calendar.messages.previous'),
      next: t('aiAssistant.meetingSummary.form.calendar.messages.next'),
      today: t('aiAssistant.meetingSummary.form.calendar.messages.today'),
      agenda: t('aiAssistant.meetingSummary.form.calendar.messages.agenda'),
      showMore: total => t('aiAssistant.meetingSummary.form.calendar.messages.showMore', { total }),
      noEventsInRange: t('aiAssistant.meetingSummary.form.calendar.messages.noEventsInRange'),
    }
  }), [t]);

  const calendarComponents = useMemo(() => ({
    event: (props) => <CustomEventWrapper {...props} onSelectEvent={onSelectEvent} windowSize={windowSize} />
  }), [onSelectEvent, windowSize]);

  return (
    <Spin spinning={loading} indicator={<LoadingAnimation />} tip={t('aiAssistant.meetingSummary.form.calendar.loadingEvents')} wrapperClassName="calendar-loading-spin">
      <div className={`meeting-calendar-container ${dark ? 'dark' : ''}`} style={{ height: '60vh' }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ minHeight: 500 }}
          onSelectEvent={() => {}} // Pass an empty function to override default click selection
          onDoubleClickEvent={() => {}} // Also override double click if it causes issues
          onKeyPressEvent={() => {}} // Override key press if needed
          onNavigate={handleNavigate}
          date={currentDate}
          views={['month', 'week', 'day', 'agenda']}
          formats={formatos}
          messages={messages}
          popup
          components={calendarComponents}
          className={dark ? 'rbc-dark' : ''} // For specific dark mode styling of RBC
        />
      </div>
    </Spin>
  );
};

export default MeetingCalendar; 